/**
 * Entitlements: which add-ons an organization has. An add-on that is off is hidden in the web shell
 * AND refused by the API. No rows for an organization = unrestricted; a new add-on is on for every
 * organization configured before it existed.
 */
export const ADDONS = [
  {
    key: 'governance',
    label: 'Data Governance',
    description:
      'Data dictionary, sources, mappings, processing rules, reference lists, quality and the pipeline designer.',
  },
  {
    key: 'workflows',
    label: 'Workflows',
    description: 'Business processes with roles, human gates and agent actors; the My Work inbox.',
  },
  {
    key: 'analytics',
    label: 'Analytics',
    description: 'Dashboards, reports and custom models over the governed data.',
  },
  { key: 'board', label: 'Board', description: 'The board of requests to the agent fleet.' },
  {
    key: 'docs',
    label: 'Docs',
    description: 'Requirement sources, requirements, tasks, evidence, the summary and their tests.',
  },
  {
    key: 'compliance',
    label: 'Compliance',
    description: 'The project audit view: runs, findings, evidence and fixes.',
  },
] as const;

export type AddonKey = (typeof ADDONS)[number]['key'];
export const ADDON_KEYS: readonly AddonKey[] = ADDONS.map((a) => a.key);
export const isAddonKey = (k: unknown): k is AddonKey => ADDON_KEYS.includes(k as AddonKey);

export interface EntitlementRow {
  addon: string;
  enabled: boolean;
}

/** null = unrestricted. */
export type Entitlements = { addons: AddonKey[] } | null;

/** One organization's rows → its entitlements. Unknown add-on rows are ignored. */
export function entitlementsFromRows(rows: readonly EntitlementRow[]): Entitlements {
  const list = rows.filter((r) => isAddonKey(r.addon));
  if (!list.length) return null;
  const off = new Set(list.filter((r) => r.enabled === false).map((r) => r.addon));
  return { addons: ADDON_KEYS.filter((k) => !off.has(k)) };
}

export function addonEnabled(ent: Entitlements, key: AddonKey): boolean {
  if (!ent) return true;
  return ent.addons.includes(key);
}

export function addonFlags(ent: Entitlements): Record<AddonKey, boolean> {
  return Object.fromEntries(ADDON_KEYS.map((k) => [k, addonEnabled(ent, k)])) as Record<AddonKey, boolean>;
}
